import { createSignal, onMount } from "solid-js";

const VISITED_PREFIX = "flowicity_visited_";

/**
 * Hook that detects if this is the user's first visit to a specific view.
 *
 * @param viewKey - Unique identifier for the view (e.g., 'board', 'backlog', 'chat')
 * @returns Signal that is true only on the first visit to this view
 *
 * @example
 * ```typescript
 * const isFirstVisit = useFirstVisit('board');
 *
 * createEffect(() => {
 *   if (isFirstVisit()) {
 *     // Show tooltips or onboarding
 *   }
 * });
 * ```
 */
export function useFirstVisit(viewKey: string): () => boolean {
  const storageKey = VISITED_PREFIX + viewKey;
  const [isFirst, setIsFirst] = createSignal(false);

  onMount(() => {
    const hasVisited = localStorage.getItem(storageKey) === "true";

    if (!hasVisited) {
      localStorage.setItem(storageKey, "true");
      setIsFirst(true);
    } else {
      setIsFirst(false);
    }
  });

  return isFirst;
}
